// In-memory real-time room state manager for SyncScreen.
//
// This holds participants, live video state, queue, polls, bookmarks, subtitles
// and reactions, and broadcasts changes to SSE listeners. It is intentionally
// process-local: for a single Node instance (this sandbox, Vercel, Railway,
// Fly.io, a VPS) it works perfectly. To scale horizontally behind a load
// balancer, swap `broadcast` for a Redis Pub/Sub fan-out (see DEPLOYMENT.md).

export interface Participant {
  id: string;
  name: string;
  avatarColor: string;
  joinedAt: number;
  isHost?: boolean;
  currentTime?: number;
  playbackState?: "playing" | "paused" | "buffering";
  lastPing?: number;
  presence?: "watching" | "idle" | "away";
}

export type VideoType = "direct" | "youtube" | "hls" | "twitch" | "vimeo";

export interface PlaylistItem {
  id: string;
  url: string;
  type: VideoType;
  title: string;
  addedBy: string;
  addedByName: string;
}

export interface VideoBookmark {
  id: string;
  time: number;
  label: string;
  createdBy: string;
  createdByName: string;
  createdAt: number;
}

// A comment pinned to a specific video timestamp (SoundCloud-style).
export interface TimeComment {
  id: string;
  time: number; // video position in seconds
  content: string;
  createdBy: string;
  createdByName: string;
  createdAt: number;
}

export interface SubtitleCue {
  id?: string;
  startTime: number;
  endTime: number;
  text: string;
}

export interface SubtitleTrack {
  id: string;
  label: string;
  language: string;
  cues: SubtitleCue[];
  offsetMs: number;
}

export interface PollOption {
  id: string;
  text: string;
  votes: string[]; // userIds
}

export interface RoomPoll {
  id: string;
  question: string;
  options: PollOption[];
  createdBy: string;
  createdByName: string;
  active: boolean;
  createdAt: number;
}

export interface VideoState {
  url: string;
  type: VideoType;
  playing: boolean;
  currentTime: number;
  playbackRate: number;
  updatedAt: number;
  updatedBy: string;
}

// A reaction is identified by an SVG icon key (no emojis).
export type ReactionIcon =
  | "heart"
  | "flame"
  | "laugh"
  | "popcorn"
  | "clap"
  | "party"
  | "wow"
  | "rocket"
  | "thumbsup"
  | "cry"
  | "angry"
  | "skull";

export interface FloatingReaction {
  id: string;
  icon: ReactionIcon;
  /** For custom (uploaded) reactions, the image URL/data URI. */
  customUrl?: string;
  userId: string;
  userName: string;
  timestamp: number;
}

export type RoomEventType =
  | "video_state"
  | "chat"
  | "system"
  | "participants"
  | "settings"
  | "ping"
  | "reaction"
  | "queue"
  | "poll"
  | "kick"
  | "host_change"
  | "bookmarks"
  | "subtitles"
  | "participant_progress"
  | "typing"
  | "hand"
  | "comments"
  | "countdown"
  | "history"
  | "custom_reactions"
  | "analytics";

export interface RoomEvent {
  type: RoomEventType;
  data: Record<string, unknown>;
  timestamp: number;
}

export interface RoomState {
  id: string;
  title: string;
  hostId: string;
  hostOnly: boolean;
  isLocked: boolean;
  videoState: VideoState;
  queue: PlaylistItem[];
  bookmarks: VideoBookmark[];
  subtitles: SubtitleTrack | null;
  activePoll: RoomPoll | null;
  participants: Map<string, Participant>;
  listeners: Map<string, (event: RoomEvent) => void>;
  lastActivity: number;
  typing: Map<string, number>; // userId -> lastTypingAt timestamp
  raisedHands: Map<string, number>; // userId -> raisedAt timestamp
  comments: TimeComment[]; // timestamped comments on the video timeline
  history: HistoryEntry[]; // log of videos that have been played
  customReactions: CustomReaction[]; // host-uploaded SVG/image reactions
  analytics: RoomAnalytics; // aggregated room stats
}

// Aggregated room analytics (tracked server-side, broadcast periodically).
export interface RoomAnalytics {
  peakViewers: number;
  totalReactions: number;
  totalMessages: number;
  totalComments: number;
  createdAt: number;
}

// A host-uploaded custom reaction (emoji-free — uses an image URL or data URI).
export interface CustomReaction {
  id: string;
  url: string; // image URL or data URI
  label: string;
  addedBy: string;
  addedByName: string;
  addedAt: number;
}

// A record of a video that was played in the room.
export interface HistoryEntry {
  id: string;
  url: string;
  type: VideoType;
  title: string;
  playedBy: string;
  playedByName: string;
  playedAt: number;
}

// Restrained, non-neon avatar palette (muted but distinguishable).
const AVATAR_COLORS = [
  "#E57373", // soft red
  "#F4A261", // warm sand
  "#E9C46A", // muted gold
  "#81B29A", // sage
  "#5B9BD5", // steel blue
  "#9B8DD9", // lavender
  "#D88BB0", // dusty pink
  "#6FB1C3", // slate cyan
  "#C58B6A", // terracotta
  "#8AAE7E", // olive
];

export function getAvatarColor(nameOrId: string): string {
  let hash = 0;
  for (let i = 0; i < nameOrId.length; i++) {
    hash = nameOrId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

const MAX_PARTICIPANTS = 100;
const ROOM_EXPIRY_MS = 5 * 60 * 1000; // empty rooms are pruned after 5 minutes
const PARTICIPANT_TIMEOUT_MS = 60 * 1000; // no ping in 60s = stale

class RoomManager {
  private rooms: Map<string, RoomState> = new Map();
  private pruneTimer: ReturnType<typeof setInterval> | null = null;

  // Lazily start a global prune loop that clears stale typing indicators.
  // Without this, a "X is typing…" indicator would persist forever once a user
  // stops typing (pruning previously only happened on the next setTyping call).
  private ensurePruneTimer(): void {
    if (this.pruneTimer) return;
    this.pruneTimer = setInterval(() => {
      const now = Date.now();
      for (const [roomId, room] of this.rooms) {
        if (!room.typing || room.typing.size === 0) continue;
        let changed = false;
        for (const [uid, ts] of room.typing) {
          if (now - ts > 4000) {
            room.typing.delete(uid);
            changed = true;
          }
        }
        if (changed) {
          const typers = Array.from(room.typing.entries())
            .filter(([uid]) => room.participants.has(uid))
            .map(([uid]) => ({ userId: uid, userName: room.participants.get(uid)?.name || "Someone" }));
          this.broadcast(roomId, { type: "typing", data: { typers }, timestamp: now });
        }
      }
    }, 2000);
    // Allow the Node process to exit even if the timer is still running.
    if (this.pruneTimer && typeof this.pruneTimer.unref === "function") {
      this.pruneTimer.unref();
    }
  }

  getRoom(roomId: string): RoomState | undefined {
    const room = this.rooms.get(roomId);
    if (!room) return undefined;
    // Defensive migration: rooms created by an older code path (e.g. a stale
    // dev-mode singleton surviving hot reload) may lack the typing/raisedHands
    // maps. Lazily initialise them so downstream code never sees `undefined`.
    if (!room.typing) room.typing = new Map();
    if (!room.raisedHands) room.raisedHands = new Map();
    if (!room.comments) room.comments = [];
    if (!room.history) room.history = [];
    if (!room.customReactions) room.customReactions = [];
    if (!room.analytics)
      room.analytics = { peakViewers: 0, totalReactions: 0, totalMessages: 0, totalComments: 0, createdAt: Date.now() };
    return room;
  }

  createRoom(
    roomId: string,
    hostId: string,
    hostOnly: boolean,
    title = "Watch Party Room"
  ): RoomState {
    const state: RoomState = {
      id: roomId,
      title,
      hostId,
      hostOnly,
      isLocked: false,
      videoState: {
        url: "",
        type: "direct",
        playing: false,
        currentTime: 0,
        playbackRate: 1,
        updatedAt: Date.now(),
        updatedBy: hostId,
      },
      queue: [],
      bookmarks: [],
      subtitles: null,
      activePoll: null,
      participants: new Map(),
      listeners: new Map(),
      lastActivity: Date.now(),
      typing: new Map(),
      raisedHands: new Map(),
      comments: [],
      history: [],
      customReactions: [],
      analytics: { peakViewers: 0, totalReactions: 0, totalMessages: 0, totalComments: 0, createdAt: Date.now() },
    };
    this.rooms.set(roomId, state);
    return state;
  }

  ensureRoom(
    roomId: string,
    hostId: string,
    hostOnly: boolean,
    title = "Watch Party Room"
  ): RoomState {
    const existing = this.getRoom(roomId);
    if (existing) return existing;
    return this.createRoom(roomId, hostId, hostOnly, title);
  }

  addParticipant(roomId: string, participant: Participant): { ok: boolean; reason?: string } {
    const room = this.getRoom(roomId);
    if (!room) return { ok: false, reason: "Room not found" };
    if (room.participants.size >= MAX_PARTICIPANTS && !room.participants.has(participant.id)) {
      return { ok: false, reason: "Room is full" };
    }
    participant.isHost = participant.id === room.hostId;
    participant.currentTime = participant.currentTime || 0;
    participant.playbackState = participant.playbackState || "paused";
    participant.lastPing = Date.now();
    room.participants.set(participant.id, participant);
    room.lastActivity = Date.now();
    // Track peak viewers
    if (room.participants.size > room.analytics.peakViewers) {
      room.analytics.peakViewers = room.participants.size;
    }
    this.broadcast(room.id, {
      type: "participants",
      data: { participants: this.getParticipantList(room.id) },
      timestamp: Date.now(),
    });
    return { ok: true };
  }

  updateParticipantProgress(
    roomId: string,
    userId: string,
    currentTime: number,
    playbackState: "playing" | "paused" | "buffering"
  ): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    const p = room.participants.get(userId);
    if (p) {
      p.currentTime = currentTime;
      p.playbackState = playbackState;
      p.lastPing = Date.now();
      p.presence = "watching";
      this.broadcast(room.id, {
        type: "participant_progress",
        data: { userId, currentTime, playbackState, participants: this.getParticipantList(room.id) },
        timestamp: Date.now(),
      });
    }
  }

  removeParticipant(roomId: string, userId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.participants.delete(userId);
    room.listeners.delete(userId);
    this.broadcast(room.id, {
      type: "participants",
      data: { participants: this.getParticipantList(room.id) },
      timestamp: Date.now(),
    });
    if (room.participants.size === 0) {
      const expiry = ROOM_EXPIRY_MS;
      setTimeout(() => {
        const r = this.getRoom(roomId);
        if (r && r.participants.size === 0) {
          this.rooms.delete(roomId);
        }
      }, expiry);
    }
  }

  getParticipantList(roomId: string): Participant[] {
    const room = this.getRoom(roomId);
    if (!room) return [];
    const now = Date.now();
    return Array.from(room.participants.values()).map((p) => {
      // Compute presence from lastPing: watching <15s, idle <45s, away otherwise.
      let presence: "watching" | "idle" | "away" = "away";
      if (p.lastPing) {
        const age = now - p.lastPing;
        if (age < 15000) presence = "watching";
        else if (age < 45000) presence = "idle";
      }
      return { ...p, isHost: p.id === room.hostId, presence };
    });
  }

  subscribe(roomId: string, userId: string, cb: (event: RoomEvent) => void): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.listeners.set(userId, cb);
  }

  unsubscribe(roomId: string, userId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.listeners.delete(userId);
  }

  broadcast(roomId: string, event: RoomEvent, excludeUserId?: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.lastActivity = Date.now();
    room.listeners.forEach((cb, uid) => {
      if (uid !== excludeUserId) {
        try {
          cb(event);
        } catch {
          /* listener gone */
        }
      }
    });
  }

  updateVideoState(roomId: string, partial: Partial<VideoState>, userId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    Object.assign(room.videoState, partial, { updatedAt: Date.now(), updatedBy: userId });
    this.broadcast(room.id, {
      type: "video_state",
      data: { ...room.videoState },
      timestamp: Date.now(),
    });
  }

  updateSettings(
    roomId: string,
    settings: { hostOnly?: boolean; isLocked?: boolean; title?: string }
  ): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    if (settings.hostOnly !== undefined) room.hostOnly = settings.hostOnly;
    if (settings.isLocked !== undefined) room.isLocked = settings.isLocked;
    if (settings.title !== undefined) room.title = settings.title;
    this.broadcast(room.id, {
      type: "settings",
      data: {
        hostOnly: room.hostOnly,
        isLocked: room.isLocked,
        title: room.title,
      },
      timestamp: Date.now(),
    });
  }

  transferHost(roomId: string, newHostId: string): boolean {
    const room = this.getRoom(roomId);
    if (!room) return false;
    if (!room.participants.has(newHostId)) return false;
    room.hostId = newHostId;
    const newHost = room.participants.get(newHostId);
    this.broadcast(room.id, {
      type: "host_change",
      data: { newHostId, newHostName: newHost?.name || "User" },
      timestamp: Date.now(),
    });
    this.broadcast(room.id, {
      type: "participants",
      data: { participants: this.getParticipantList(room.id) },
      timestamp: Date.now(),
    });
    return true;
  }

  // ---- Queue ----
  addToQueue(roomId: string, item: PlaylistItem): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.queue.push(item);
    this.broadcast(room.id, { type: "queue", data: { queue: room.queue }, timestamp: Date.now() });
  }

  removeFromQueue(roomId: string, itemId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.queue = room.queue.filter((i) => i.id !== itemId);
    this.broadcast(room.id, { type: "queue", data: { queue: room.queue }, timestamp: Date.now() });
  }

  // Reorder the queue to match the given ordered list of item ids.
  reorderQueue(roomId: string, orderedIds: string[]): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    const map = new Map(room.queue.map((i) => [i.id, i]));
    const next: PlaylistItem[] = [];
    for (const id of orderedIds) {
      const item = map.get(id);
      if (item) {
        next.push(item);
        map.delete(id);
      }
    }
    // append any items not in orderedIds (defensive — shouldn't happen)
    for (const item of map.values()) next.push(item);
    room.queue = next;
    this.broadcast(room.id, { type: "queue", data: { queue: room.queue }, timestamp: Date.now() });
  }

  playNextInQueue(roomId: string, userId: string): PlaylistItem | null {
    const room = this.getRoom(roomId);
    if (!room || room.queue.length === 0) return null;
    const nextItem = room.queue.shift()!;
    this.updateVideoState(
      room.id,
      {
        url: nextItem.url,
        type: nextItem.type,
        playing: true,
        currentTime: 0,
        playbackRate: 1,
      },
      userId
    );
    this.broadcast(room.id, { type: "queue", data: { queue: room.queue }, timestamp: Date.now() });
    return nextItem;
  }

  clearQueue(roomId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.queue = [];
    this.broadcast(room.id, { type: "queue", data: { queue: [] }, timestamp: Date.now() });
  }

  // ---- Bookmarks ----
  addBookmark(roomId: string, bookmark: VideoBookmark): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.bookmarks.push(bookmark);
    room.bookmarks.sort((a, b) => a.time - b.time);
    this.broadcast(room.id, { type: "bookmarks", data: { bookmarks: room.bookmarks }, timestamp: Date.now() });
  }

  removeBookmark(roomId: string, bookmarkId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.bookmarks = room.bookmarks.filter((b) => b.id !== bookmarkId);
    this.broadcast(room.id, { type: "bookmarks", data: { bookmarks: room.bookmarks }, timestamp: Date.now() });
  }

  // ---- Subtitles ----
  setSubtitles(roomId: string, subtitles: SubtitleTrack | null): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.subtitles = subtitles;
    this.broadcast(room.id, { type: "subtitles", data: { subtitles: room.subtitles }, timestamp: Date.now() });
  }

  updateSubtitleOffset(roomId: string, offsetMs: number): void {
    const room = this.getRoom(roomId);
    if (!room || !room.subtitles) return;
    room.subtitles.offsetMs = offsetMs;
    this.broadcast(room.id, { type: "subtitles", data: { subtitles: room.subtitles }, timestamp: Date.now() });
  }

  // ---- Reactions ----
  sendReaction(roomId: string, reaction: FloatingReaction): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.analytics.totalReactions++;
    this.broadcast(room.id, {
      type: "reaction",
      data: { ...reaction } as Record<string, unknown>,
      timestamp: Date.now(),
    });
  }

  // ---- Polls ----
  createPoll(roomId: string, poll: RoomPoll): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.activePoll = poll;
    this.broadcast(room.id, { type: "poll", data: { poll: room.activePoll }, timestamp: Date.now() });
  }

  votePoll(roomId: string, optionId: string, userId: string): void {
    const room = this.getRoom(roomId);
    if (!room || !room.activePoll || !room.activePoll.active) return;
    room.activePoll.options.forEach((opt) => {
      opt.votes = opt.votes.filter((uid) => uid !== userId);
    });
    const target = room.activePoll.options.find((opt) => opt.id === optionId);
    if (target) target.votes.push(userId);
    this.broadcast(room.id, { type: "poll", data: { poll: room.activePoll }, timestamp: Date.now() });
  }

  closePoll(roomId: string): void {
    const room = this.getRoom(roomId);
    if (!room || !room.activePoll) return;
    room.activePoll.active = false;
    this.broadcast(room.id, { type: "poll", data: { poll: room.activePoll }, timestamp: Date.now() });
  }

  // ---- Typing indicators ----
  // Records a typing pulse from a user and broadcasts it. Stale typing entries
  // (older than 4s) are pruned on each call so the indicator auto-clears.
  setTyping(roomId: string, userId: string, userName: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    this.ensurePruneTimer();
    const now = Date.now();
    room.typing.set(userId, now);
    // prune stale
    for (const [uid, ts] of room.typing) {
      if (now - ts > 4000) room.typing.delete(uid);
    }
    const typers = Array.from(room.typing.entries())
      .filter(([uid]) => room.participants.has(uid))
      .map(([uid, _ts]) => ({ userId: uid, userName: room.participants.get(uid)?.name || "Someone" }));
    this.broadcast(room.id, {
      type: "typing",
      data: { typers },
      timestamp: now,
    });
  }

  // ---- Raise / lower hand ----
  toggleHand(roomId: string, userId: string): boolean {
    const room = this.getRoom(roomId);
    if (!room) return false;
    if (room.raisedHands.has(userId)) {
      room.raisedHands.delete(userId);
    } else {
      room.raisedHands.set(userId, Date.now());
    }
    const hands = Object.fromEntries(room.raisedHands);
    this.broadcast(room.id, { type: "hand", data: { raisedHands: hands }, timestamp: Date.now() });
    return room.raisedHands.has(userId);
  }

  lowerHand(roomId: string, userId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.raisedHands.delete(userId);
    const hands = Object.fromEntries(room.raisedHands);
    this.broadcast(room.id, { type: "hand", data: { raisedHands: hands }, timestamp: Date.now() });
  }

  // ---- Time-pinned comments (SoundCloud-style timeline comments) ----
  addComment(roomId: string, comment: TimeComment): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.comments.push(comment);
    room.comments.sort((a, b) => a.time - b.time);
    room.analytics.totalComments++;
    this.broadcast(room.id, {
      type: "comments",
      data: { comments: room.comments },
      timestamp: Date.now(),
    });
  }

  removeComment(roomId: string, commentId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.comments = room.comments.filter((c) => c.id !== commentId);
    this.broadcast(room.id, {
      type: "comments",
      data: { comments: room.comments },
      timestamp: Date.now(),
    });
  }

  // ---- Synced countdown (3-2-1 group start) ----
  // Broadcasts a target timestamp; clients show a countdown and play when it
  // hits zero. The `durationMs` lets clients display the right starting number.
  startCountdown(roomId: string, durationMs = 3000): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    const endsAt = Date.now() + durationMs;
    this.broadcast(room.id, {
      type: "countdown",
      data: { endsAt, durationMs },
      timestamp: Date.now(),
    });
  }

  // ---- Now-playing history log ----
  // Records a video that was loaded/played. Capped at 50 entries.
  addToHistory(roomId: string, entry: HistoryEntry): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    // Don't duplicate if the same URL was just played
    if (room.history.length > 0 && room.history[0].url === entry.url) return;
    room.history.unshift(entry);
    if (room.history.length > 50) room.history = room.history.slice(0, 50);
    this.broadcast(room.id, {
      type: "history",
      data: { history: room.history },
      timestamp: Date.now(),
    });
  }

  // ---- Custom reactions (host-uploaded, emoji-free) ----
  addCustomReaction(roomId: string, reaction: CustomReaction): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    if (room.customReactions.length >= 12) return; // cap at 12 custom reactions
    room.customReactions.push(reaction);
    this.broadcast(room.id, {
      type: "custom_reactions",
      data: { customReactions: room.customReactions },
      timestamp: Date.now(),
    });
  }

  removeCustomReaction(roomId: string, reactionId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.customReactions = room.customReactions.filter((r) => r.id !== reactionId);
    this.broadcast(room.id, {
      type: "custom_reactions",
      data: { customReactions: room.customReactions },
      timestamp: Date.now(),
    });
  }

  // ---- Analytics ----
  trackMessage(roomId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    room.analytics.totalMessages++;
  }

  getAnalytics(roomId: string): RoomAnalytics | null {
    const room = this.getRoom(roomId);
    if (!room) return null;
    return { ...room.analytics };
  }

  broadcastAnalytics(roomId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    this.broadcast(room.id, {
      type: "analytics",
      data: { analytics: { ...room.analytics } },
      timestamp: Date.now(),
    });
  }

  // ---- Maintenance ----
  pruneStaleParticipants(roomId: string): void {
    const room = this.getRoom(roomId);
    if (!room) return;
    const now = Date.now();
    let changed = false;
    for (const [uid, p] of room.participants) {
      if (uid === room.hostId) continue;
      if (p.lastPing && now - p.lastPing > PARTICIPANT_TIMEOUT_MS) {
        room.participants.delete(uid);
        room.listeners.delete(uid);
        changed = true;
      }
    }
    if (changed) {
      this.broadcast(room.id, {
        type: "participants",
        data: { participants: this.getParticipantList(room.id) },
        timestamp: now,
      });
    }
  }
}

// Version-gated singleton: when the RoomState shape changes (new fields,
// new methods), bump ROOM_MANAGER_VERSION so a stale dev-mode singleton
// surviving hot reload is replaced with a fresh instance instead of crashing
// on missing fields/methods. Production never hot-reloads so this is a no-op
// there.
const ROOM_MANAGER_VERSION = 10;

const globalForRooms = globalThis as typeof globalThis & {
  __roomManager?: RoomManager;
  __roomManagerVersion?: number;
};

export const roomManager =
  globalForRooms.__roomManager && globalForRooms.__roomManagerVersion === ROOM_MANAGER_VERSION
    ? globalForRooms.__roomManager
    : new RoomManager();

if (process.env.NODE_ENV !== "production") {
  globalForRooms.__roomManager = roomManager;
  globalForRooms.__roomManagerVersion = ROOM_MANAGER_VERSION;
}
